import { Avatar, Badge, Button, Card, Chip, Drawer, EmptyState, Skeleton, formatDate, formatTime, useToast } from "@helmr/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { rescheduleAppointment, updateAppointment, type AppointmentAction, type AppointmentItem } from "../../api/appointments";
import { NoAccess } from "../../app/NoAccess";
import { useServices } from "../../app/services";
import { addDays, minutesOfDay, today as localDate } from "../../lib/dates";
import { addMinutesHHMM, hhmmOf, zonedToInstant } from "../../lib/zoned";
import { FailureBody } from "../home/states";
import { useInvalidateLeadData } from "../leads/hooks";
import { SegmentedControl } from "@helmr/ui";
import { WorkNav } from "./Nav";
import { useAppointmentDays, useDateParam, useScope, weekStart, type Scope } from "./hooks";
import { fu } from "./strings";
import s from "./followups.module.css";

const t = fu.appointments;
const TONE = { booked: "info", in: "ok", no_show: "crit", done: "ok", cancelled: "neutral" } as const;

/** Writes are gated by the linked record, as the API checks: marking a PT session for PT, else edit on the lead or member. */
const mayChange = (a: AppointmentItem, sc: Scope) => sc.ctx.has(a.source === "pt" ? "pt.complete" : a.person.type === "lead" ? "leads.edit" : "members.edit_profile");

function actionsFor(a: AppointmentItem): Array<{ action: AppointmentAction; label: string; primary?: boolean }> {
  if (a.status === "done" || a.status === "cancelled" || a.status === "no_show") return [];
  switch (a.source) {
    case "pt": return [{ action: "no_show", label: t.actions.no_show }, { action: "done", label: t.actions.done, primary: true }];
    case "trial": return [{ action: "no_show", label: t.actions.no_show }, { action: "done", label: t.actions.trialDone, primary: true }];
    case "appointment": return [
      { action: "cancelled", label: t.actions.cancelled }, { action: "no_show", label: t.actions.no_show },
      ...(a.status === "booked" ? [{ action: "arrived" as const, label: t.actions.arrived }] : []),
      { action: "done", label: t.actions.done, primary: true },
    ];
  }
}

export function AppointmentsPage() {
  const sc = useScope();
  const { date, setDate, params, setParams } = useDateParam(sc);
  const view = params.get("view") === "week" ? "week" : "day";
  const days = useMemo(() => (view === "week" ? Array.from({ length: 7 }, (_, i) => addDays(weekStart(date), i)) : [date]), [view, date]);
  const results = useAppointmentDays(sc, days);
  const [open, setOpen] = useState<AppointmentItem | null>(null);
  const step = view === "week" ? 7 : 1;

  if (sc.ctx.status !== "loading" && !sc.ctx.has("leads.view") && !sc.ctx.has("pt.view") && !sc.ctx.has("members.view")) return <NoAccess />;

  const pending = results.some((r) => r.isPending);
  const error = results.find((r) => r.error)?.error;
  const all = results.flatMap((r) => r.data?.items ?? []);
  const omitted = [...new Set(results.flatMap((r) => r.data?.omitted ?? []))];
  const truncated = results.some((r) => r.data?.truncated);

  return (
    <div className={s.page}>
      <title>Appointments · Helmr</title>
      <div className={s.head}>
        <div><h1 tabIndex={-1}>{t.title}</h1><p>{pending ? " " : t.sub(all.length)}</p></div>
        <WorkNav />
      </div>
      <div className={s.filters} role="group" aria-label="Appointment range">
        <SegmentedControl label={t.views.label} value={view} onChange={(v) => { const n = new URLSearchParams(params); if (v === "week") n.set("view", "week"); else n.delete("view"); setParams(n, { replace: true }); }} options={[{ value: "day", label: t.views.day }, { value: "week", label: t.views.week }]} />
        <Button size="sm" onClick={() => setDate(addDays(date, -step))} aria-label={t.prev}>←</Button>
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label={t.date} />
        <Button size="sm" onClick={() => setDate(addDays(date, step))} aria-label={t.next}>→</Button>
        {date !== sc.date ? <Button size="sm" onClick={() => setDate(sc.date)}>{t.today}</Button> : null}
        {sc.ctx.has("leads.edit") ? <Button as={Link} to="/leads/trial" size="sm" variant="primary">Book</Button> : null}
      </div>
      {omitted.map((f) => <p className={s.note} key={f} role="note">{t.omitted(f === "pt_calendar" ? "PT sessions" : f)}</p>)}
      {truncated ? <p className={s.note}>{t.truncated}</p> : null}

      {pending ? <Card><div aria-busy="true" data-testid="appointments-skeleton">{[0, 1, 2, 3].map((i) => <div className={s.skRow} key={i}><Skeleton width={44} height={14} /><div style={{ display: "grid", gap: 6 }}><Skeleton width="45%" height={13} /><Skeleton width="70%" height={12} /></div></div>)}</div></Card>
        : error ? <Card><FailureBody error={error} title={t.error} what="Appointments" onRetry={() => results.forEach((r) => void r.refetch())} /></Card>
        : view === "day" ? <Day sc={sc} date={date} items={results[0]?.data?.items ?? []} onOpen={setOpen} />
        : <Week sc={sc} days={days} items={results.map((r) => r.data?.items ?? [])} onOpen={setOpen} />}

      <ApptDrawer appointment={open} onClose={() => setOpen(null)} sc={sc} />
    </div>
  );
}

function Day({ sc, date, items, onOpen }: { sc: Scope; date: string; items: AppointmentItem[]; onOpen: (a: AppointmentItem) => void }) {
  const { timeZone } = sc.ctx;
  const sorted = useMemo(() => [...items].sort((a, b) => a.startsAt - b.startsAt), [items]);
  const isToday = date === sc.date;
  const nowMin = minutesOfDay(sc.nowMs, timeZone);
  const startMin = (a: AppointmentItem) => minutesOfDay(a.startsAt, timeZone);
  const nowIndex = isToday ? sorted.findIndex((a) => startMin(a) >= nowMin) : -1;
  const next = isToday ? sorted.find((a) => a.status === "booked" && startMin(a) >= nowMin) : undefined;
  const nowRef = useRef<HTMLDivElement>(null);
  useEffect(() => { nowRef.current?.scrollIntoView?.({ block: "center" }); }, [sorted.length]);
  if (sorted.length === 0) return <Card><EmptyState icon="trial" title={t.empty} description={t.emptyBody} /></Card>;
  return (
    <Card aria-label={formatDate(date, sc.ctx.locale, "full")}>
      <div className={s.timeline}>
        {sorted.map((a, i) => (
          <div key={`${a.source}:${a.id}`} style={{ display: "contents" }}>
            {i === nowIndex ? <div className={s.nowl} ref={nowRef} aria-hidden="true"><span>{t.now(formatTime(sc.nowMs, timeZone))}</span></div> : null}
            <div className={`${s.ap} ${isToday && startMin(a) < nowMin ? s.past : ""} ${a === next ? s.next : ""}`} data-testid="appointment-row">
              <b>{formatTime(a.startsAt, timeZone)}</b>
              <span className={`${s.node} ${a.status === "in" ? s.in : a.status === "no_show" ? s.no_show : ""}`} />
              <button className={s.apBtn} onClick={() => onOpen(a)} aria-haspopup="dialog">
                <b title={a.person.name ?? ""}>{a.person.name ?? "Unnamed"}</b> <span className={s.muted}>· {a.kind}</span>
                {a.staff ? <span className={s.meta}><Avatar id={a.staff.id} name={a.staff.name} size="xs" />{a.staff.name}</span> : null}
              </button>
              <Badge tone={TONE[a.status]} dot>{t.status[a.status]}</Badge>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Week({ sc, days, items, onOpen }: { sc: Scope; days: string[]; items: AppointmentItem[][]; onOpen: (a: AppointmentItem) => void }) {
  const { timeZone, locale } = sc.ctx;
  return (
    <div className={s.week} role="group" aria-label="Week">
      {days.map((d, i) => {
        const list = [...(items[i] ?? [])].sort((a, b) => a.startsAt - b.startsAt);
        return (
          <section key={d} className={`${s.wday} ${d === sc.date ? s.wtoday : ""}`} aria-label={formatDate(d, locale, "weekday")}>
            <h2>{formatDate(d, locale, "weekday")} <span className={s.wcount}>{list.length > 99 ? "99+" : list.length}</span></h2>
            {list.length === 0 ? <p className={s.wempty}>{t.empty}</p> : list.map((a) => (
              <button key={`${a.source}:${a.id}`} className={s.wcard} onClick={() => onOpen(a)} data-status={a.status} aria-haspopup="dialog">
                <b>{formatTime(a.startsAt, timeZone)}</b>
                <span title={a.person.name ?? ""}>{a.person.name ?? "Unnamed"}</span>
                <Chip kind={a.kind === "Trial" ? "trial" : a.kind === "PT" ? "renewal" : "enquiry"}>{a.kind}</Chip>
              </button>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function ApptDrawer({ appointment, onClose, sc }: { appointment: AppointmentItem | null; onClose: () => void; sc: Scope }) {
  const { client } = useServices();
  const refresh = useInvalidateLeadData();
  const toast = useToast();
  const { locale, timeZone } = sc.ctx;
  const last = useRef<AppointmentItem | null>(null);
  if (appointment) last.current = appointment;
  const a = appointment ?? last.current;
  const [busy, setBusy] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    setMoving(false);
    if (appointment) { setDate(localDate(appointment.startsAt, timeZone)); setTime(hhmmOf(appointment.startsAt, timeZone)); }
  }, [appointment, timeZone]);

  async function go(action: AppointmentAction) {
    if (!a) return;
    setBusy(action);
    try {
      await updateAppointment(client, a, action, new Date(sc.nowMs).toISOString());
      await refresh(); // a trial's outcome lives on the lead
      toast.show(t.drawer.updated);
      onClose();
    } catch { toast.show(t.drawer.failed); } finally { setBusy(null); }
  }
  async function move(e: React.FormEvent) {
    e.preventDefault();
    if (!a || !date || !time) return;
    setBusy("move");
    try {
      const startsAtMs = zonedToInstant(date, time, timeZone);
      const minutes = Math.max(5, Math.round((a.endsAt - a.startsAt) / 60_000));
      await rescheduleAppointment(client, a, { startsAtMs, date, start: time, end: addMinutesHHMM(time, minutes) });
      await refresh();
      toast.show(t.resched.moved);
      onClose();
    } catch { toast.show(t.resched.failed); } finally { setBusy(null); }
  }

  const allowed = !!a && mayChange(a, sc);
  const acts = a && allowed ? actionsFor(a) : [];
  const canMove = allowed && (a.status === "booked" || a.status === "in");
  const profile = a ? (a.person.type === "lead" ? `/leads/${a.person.id}` : `/members/${a.person.id}`) : "/";
  return (
    <Drawer
      open={!!appointment}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={a ? `${a.person.name ?? "Unnamed"} · ${a.kind}` : ""}
      description={a ? `${formatDate(localDate(a.startsAt, timeZone), locale, "weekday")} at ${formatTime(a.startsAt, timeZone)}` : undefined}
      footer={moving ? null : <>
        <Button onClick={onClose}>{t.drawer.close}</Button>
        {canMove ? <Button onClick={() => setMoving(true)} disabled={busy !== null}>{t.reschedule}</Button> : null}
        {acts.map((x) => <Button key={x.action} variant={x.primary ? "primary" : "default"} busy={busy === x.action} busyLabel="Saving…" disabled={busy !== null} onClick={() => void go(x.action)}>{x.label}</Button>)}
      </>}
    >
      {a ? (
        <div className={s.drawerKv}>
          <div><small>{t.drawer.who}</small><b>{a.person.name ?? "Unnamed"}</b> <Chip kind={a.person.type === "lead" ? "enquiry" : "renewal"}>{a.person.type === "lead" ? t.drawer.lead : t.drawer.member}</Chip> <Link to={profile}>{t.drawer.open}</Link></div>
          <div><small>{t.drawer.when}</small><b>{formatTime(a.startsAt, timeZone)} – {formatTime(a.endsAt, timeZone)}</b></div>
          {a.staff ? <div><small>{t.drawer.with}</small><b>{a.staff.name}</b></div> : null}
          <div><small>{t.drawer.status}</small><Badge tone={TONE[a.status]} dot>{t.status[a.status]}</Badge></div>
          {moving ? (
            <form className={s.form} onSubmit={move}>
              <h3>{t.resched.title}</h3>
              <label className={s.fieldRow}><span>{t.resched.date}</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></label>
              <label className={s.fieldRow}><span>{t.resched.time}</span><input type="time" value={time} onChange={(e) => setTime(e.target.value)} required /></label>
              <div className={s.formActions}><Button onClick={() => setMoving(false)} disabled={busy !== null}>{t.resched.back}</Button><Button variant="primary" type="submit" busy={busy === "move"} busyLabel={t.resched.saving}>{t.resched.save}</Button></div>
            </form>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}
